import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  Home, TrendingUp, TrendingDown, FileText, Building, Users, Briefcase,
  Box, CalendarCheck, Megaphone, Vote, FolderOpen, UserCheck, MessageSquare,
  Shield, Menu, LogOut, Bell, ChevronLeft, CreditCard, AlertTriangle, Receipt, CheckSquare, ClipboardList, DollarSign
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { roleColors } from '../utils/formatters';

const menuItems = [
  { path: '/', icon: Home, label: 'Dashboard', roles: ['ADMIN', 'PRESIDENTE', 'TESORERO', 'SECRETARIO', 'PROPIETARIO'] },

  // Separador - Zona Propietario
  { type: 'separator', label: 'ZONA PROPIETARIO', roles: ['PROPIETARIO'] },

  // Opciones de propietario
  { path: '/mi-departamento', icon: CreditCard, label: 'Mi Departamento', roles: ['PROPIETARIO'], requiresDepartamento: true },
  { path: '/mis-pagos', icon: Receipt, label: 'Mis Pagos', roles: ['PROPIETARIO'], requiresDepartamento: true },
  { path: '/flujo-caja', icon: DollarSign, label: 'Flujo de Caja', roles: ['PROPIETARIO'] },
  { path: '/balances', icon: FileText, label: 'Balances', roles: ['PROPIETARIO'] },

  // Separador - Zona Administrativa
  { type: 'separator', label: 'ZONA ADMINISTRATIVA', roles: ['ADMIN', 'PRESIDENTE', 'TESORERO', 'SECRETARIO'] },

  // Módulos principales
  { path: '/finanzas', icon: DollarSign, label: 'Finanzas', roles: ['ADMIN', 'PRESIDENTE', 'TESORERO'] },
  { path: '/comunidad', icon: Users, label: 'Comunidad', roles: ['ADMIN', 'PRESIDENTE', 'TESORERO', 'SECRETARIO', 'PROPIETARIO'] },
  { path: '/operaciones', icon: ClipboardList, label: 'Operaciones', roles: ['ADMIN', 'PRESIDENTE', 'TESORERO', 'SECRETARIO', 'PORTERO'] },
  { path: '/control-acceso', icon: Shield, label: 'Control de Acceso', roles: ['ADMIN', 'PRESIDENTE', 'SECRETARIO', 'PORTERO'] },
  { path: '/proveedores', icon: Briefcase, label: 'Proveedores', roles: ['ADMIN', 'PRESIDENTE', 'TESORERO'] },
  { path: '/administracion', icon: Building, label: 'Administración', roles: ['ADMIN', 'PRESIDENTE', 'TESORERO', 'SECRETARIO'] }
];

const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { usuario, logout } = useAuth();
  const navigate = useNavigate();

  // Filtrar menú: por rol O si requiere departamento y el usuario tiene uno
  const filteredMenu = menuItems.filter(item => {
    // Si el item requiere departamento, verificar que el usuario tenga uno
    if (item.requiresDepartamento) {
      return usuario?.departamento_id != null;
    }
    // Verificar por rol
    return item.roles.includes(usuario?.rol) || usuario?.rol === 'ADMIN';
  });

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-slate-950 flex">
      {/* Overlay móvil */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-40 
        bg-slate-900 border-r border-slate-800 
        transform transition-all duration-300 ease-in-out
        ${mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        ${sidebarOpen ? 'w-64' : 'lg:w-20'}
      `}>
        {/* Logo */}
        <div className="p-4 border-b border-slate-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center flex-shrink-0">
              <Building className="text-white" size={20} />
            </div>
            {sidebarOpen && (
              <div className="overflow-hidden">
                <h1 className="font-bold text-white truncate">FlujoCaja</h1>
                <p className="text-xs text-slate-500">Administración</p>
              </div>
            )}
          </div>
        </div>

        {/* Toggle button */}
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="hidden lg:flex absolute -right-3 top-20 w-6 h-6 bg-slate-800 border border-slate-700 rounded-full items-center justify-center hover:bg-slate-700 transition-colors"
        >
          <ChevronLeft size={14} className={`text-slate-400 transition-transform ${!sidebarOpen ? 'rotate-180' : ''}`} />
        </button>

        {/* Menu */}
        <nav className="p-3 space-y-1 overflow-y-auto h-[calc(100vh-180px)]">
          {filteredMenu.map((item, index) => {
            // Si es un separador
            if (item.type === 'separator') {
              return (
                <div key={`separator-${index}`} className="pt-4 pb-2">
                  {sidebarOpen && (
                    <div className="px-3">
                      <div className="flex items-center gap-2">
                        <div className="h-px bg-slate-700 flex-1"></div>
                        <span className="text-xs font-semibold text-slate-500">{item.label}</span>
                        <div className="h-px bg-slate-700 flex-1"></div>
                      </div>
                    </div>
                  )}
                  {!sidebarOpen && (
                    <div className="h-px bg-slate-700 mx-2"></div>
                  )}
                </div>
              );
            }

            // Si es un item normal
            return (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={() => setMobileOpen(false)}
                className={({ isActive }) => `
                  flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all
                  ${isActive
                    ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                    : 'text-slate-400 hover:bg-slate-800 hover:text-white'}
                `}
              >
                <item.icon size={20} className="flex-shrink-0" />
                {sidebarOpen && <span className="font-medium truncate">{item.label}</span>}
              </NavLink>
            );
          })}
        </nav>

        {/* User section */}
        <div className="absolute bottom-0 left-0 right-0 p-3 border-t border-slate-800 bg-slate-900">
          {sidebarOpen && usuario && (
            <div className="flex items-center gap-3 mb-3 p-2">
              <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${roleColors[usuario.rol]} flex items-center justify-center text-sm font-bold text-white flex-shrink-0`}>
                {usuario.avatar || usuario.nombre?.substring(0, 2).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">{usuario.nombre}</p>
                <p className="text-xs text-slate-500">{usuario.rol}</p>
              </div>
            </div>
          )}
          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-3 px-3 py-2.5 rounded-xl text-slate-400 hover:bg-slate-800 hover:text-white transition-all"
          >
            <LogOut size={20} />
            {sidebarOpen && <span>Cerrar Sesión</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen w-full">
        {/* Header */}
        <header className="bg-slate-900/80 backdrop-blur-xl border-b border-slate-800 sticky top-0 z-20">
          <div className="flex items-center justify-between px-4 lg:px-6 py-4">
            <button
              onClick={() => setMobileOpen(true)}
              className="p-2 rounded-lg hover:bg-slate-800 text-slate-400 lg:hidden"
            >
              <Menu size={24} />
            </button>

            <div className="flex-1" />

            <div className="flex items-center gap-4">
              <button className="p-2 rounded-lg hover:bg-slate-800 text-slate-400 relative">
                <Bell size={20} />
                <span className="absolute top-1 right-1 w-2 h-2 bg-rose-500 rounded-full" />
              </button>
              <div className="hidden sm:flex items-center gap-2 text-sm text-slate-400">
                <span>{new Date().toLocaleDateString('es-PE', { weekday: 'long', day: 'numeric', month: 'long' })}</span>
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-4 lg:p-6 overflow-auto">
          {children}
        </main>
      </div>
    </div>
  );
};

export default Layout;
